import { useEffect, useMemo, useRef, useState } from 'react';
import { MapPin } from 'lucide-react';
import { useCities } from '../api/use-marketplace';
import { detectCountryDomain } from '../lib/country-domain';

/**
 * Free-text city input with a dropdown of cities we have salons in.
 * Renders as a regular text input — the suggestions are progressive
 * enhancement, not a hard select. Typing filters the list; clicking
 * a suggestion fills the input and submits.
 *
 * Two styles: `style="pill"` for the standalone search-page pill,
 * `style="bare"` for the home hero where it sits inside a combined
 * search bar without its own border.
 */
export function CityCombobox({
  value,
  onChange,
  onSubmit,
  placeholder = 'City or postcode',
  style = 'pill',
  iconWidth = 'w-4 h-4',
}: {
  value: string;
  onChange: (v: string) => void;
  onSubmit?: (v: string) => void;
  placeholder?: string;
  style?: 'pill' | 'bare';
  iconWidth?: string;
}) {
  // On a country domain, scope suggestions to that country so the dropdown
  // doesn't surface cities from other countries (results are already
  // country-filtered via useSearch). null on salunas.com/localhost → all.
  const cities = useCities(detectCountryDomain().code);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Filter — case-insensitive prefix preferred, then substring.
  const filtered = useMemo(() => {
    if (!cities.data) return [];
    const q = value.trim().toLowerCase();
    if (!q) return cities.data.slice(0, 8);
    const prefix = cities.data.filter((c) => c.name.toLowerCase().startsWith(q));
    const substr = cities.data.filter(
      (c) => !c.name.toLowerCase().startsWith(q) && c.name.toLowerCase().includes(q),
    );
    return [...prefix, ...substr].slice(0, 8);
  }, [cities.data, value]);

  // Close on outside click.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const commit = (v: string) => {
    onChange(v);
    onSubmit?.(v);
    setOpen(false);
    inputRef.current?.blur();
  };

  const wrapClass =
    style === 'pill'
      ? 'flex-1 flex items-center gap-2.5 px-4 py-2.5 rounded-xl border border-border/60 bg-card focus-within:border-primary/40 focus-within:ring-1 focus-within:ring-primary/20 transition-colors'
      : 'flex-1 flex items-center gap-3 px-4 py-3 md:py-2 cursor-text group';

  const inputClass =
    style === 'pill'
      ? 'w-full bg-transparent text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none'
      : 'w-full bg-transparent text-sm md:text-base text-foreground placeholder:text-muted-foreground/70 focus:outline-none';

  return (
    <div ref={wrapRef} className="relative flex-1">
      <label className={wrapClass}>
        <span className="flex-shrink-0">
          <MapPin className={`${iconWidth} text-muted-foreground`} />
        </span>
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setOpen(true);
            setHighlight(-1);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setHighlight((h) => Math.min(h + 1, filtered.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setHighlight((h) => Math.max(h - 1, -1));
            } else if (e.key === 'Enter') {
              e.preventDefault();
              if (highlight >= 0 && filtered[highlight]) {
                commit(filtered[highlight].name);
              } else {
                commit(value);
              }
            } else if (e.key === 'Escape') {
              setOpen(false);
            }
          }}
          onBlur={() => {
            // Defer to allow click-on-option to fire first.
            setTimeout(() => onSubmit?.(value), 150);
          }}
          placeholder={placeholder}
          autoComplete="off"
          className={inputClass}
        />
      </label>

      {open && filtered.length > 0 && (
        <ul
          className="absolute left-0 right-0 top-full mt-2 max-h-72 overflow-y-auto rounded-xl border border-border/60 bg-card shadow-lg z-50"
          role="listbox"
        >
          {filtered.map((c, i) => {
            const active = i === highlight;
            return (
              <li
                key={c.name}
                role="option"
                aria-selected={active}
                onMouseEnter={() => setHighlight(i)}
                onMouseDown={(e) => {
                  // mousedown beats blur — keeps focus until we commit
                  e.preventDefault();
                  commit(c.name);
                }}
                className={[
                  'flex items-center justify-between px-4 py-2.5 cursor-pointer text-sm',
                  active ? 'bg-primary/[0.08] text-primary' : 'text-foreground hover:bg-secondary',
                ].join(' ')}
              >
                <span className="flex items-center gap-2">
                  <MapPin className="w-3.5 h-3.5 opacity-60" />
                  {c.name}
                </span>
                <span className="text-xs text-muted-foreground tabular-nums">
                  {c.salonCount}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
